// ============================================================
// odpt:Railway の線分形状を取得・キャッシュ
// ============================================================
// ODPT のデフォルトレスポンスには geometry が含まれない。代わりに
// odpt:stationOrder (駅 ID の配列) を使い、駅マスタの座標と組み合わせて
// "駅と駅を結ぶ直線" として polyline を構築する。
// 本物の曲線形状にはならないが、PoC の地図 view としては十分。

import { ODPT_ORIGIN, ODPT_PREFIX, OPERATORS } from '../config'

export interface RailwayShape {
  id: string
  title: string
  operator: string
  /** [lon, lat] の配列 */
  coordinates: [number, number][]
}

const CACHE_KEY = 'odpt-railway-shapes-v3'  // v3: TX/TWR を含む RAIL_OPERATORS 全網羅
const CACHE_TTL_MS = 24 * 60 * 60 * 1000

interface RailwayShapesCache {
  shapes: RailwayShape[]
  fetchedAt: number
}

export type LogFn = (msg: string) => void

export class RailwayShapeStore {
  private shapes: RailwayShape[] = []
  private loadingPromise: Promise<void> | null = null
  private logger: LogFn = () => {}
  /** station ID → [lon, lat] */
  private stationCoords = new Map<string, [number, number]>()

  setLogger(fn: LogFn): void { this.logger = fn }

  /** 駅座標 lookup を設定 (これがないと shape は構築できない) */
  setStationCoords(map: Map<string, [number, number]>): void {
    this.stationCoords = map
  }

  async load(operatorIds: string[] = [OPERATORS.toei, OPERATORS.tokyoMetro, OPERATORS.jrEast]): Promise<void> {
    if (this.loadingPromise) return this.loadingPromise
    this.loadingPromise = (async () => {
      // キャッシュ — 空キャッシュは無視
      const cached = this.readCache()
      if (cached && cached.shapes.length > 0 && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
        this.shapes = cached.shapes
        this.logger(`Railway: cache hit ${this.shapes.length} lines`)
        return
      }

      if (this.stationCoords.size === 0) {
        this.logger('Railway: stationCoords 未設定。setStationCoords() を先に呼ぶこと')
        return
      }

      const q = encodeURIComponent(operatorIds.join(','))
      const url = `${ODPT_ORIGIN}${ODPT_PREFIX}/api/v4/odpt:Railway?odpt:operator=${q}`
      const res = await fetch(url)
      if (!res.ok) throw new Error(`Railway fetch failed: ${res.status}`)
      const raw = (await res.json()) as any[]
      this.logger(`Railway raw: ${raw.length} items, station coords: ${this.stationCoords.size}`)

      let totalMatched = 0
      for (const r of raw) {
        const shape = this.materialize(r)
        if (shape) {
          this.shapes.push(shape)
          totalMatched += shape.coordinates.length
        }
      }

      this.logger(`Railway built: ${this.shapes.length} lines, ${totalMatched} points`)
      this.writeCache({ shapes: this.shapes, fetchedAt: Date.now() })
    })()
    return this.loadingPromise
  }

  shapesInBBox(minLon: number, minLat: number, maxLon: number, maxLat: number): RailwayShape[] {
    const out: RailwayShape[] = []
    for (const s of this.shapes) {
      for (const [lon, lat] of s.coordinates) {
        if (lon >= minLon && lon <= maxLon && lat >= minLat && lat <= maxLat) {
          out.push(s)
          break
        }
      }
    }
    return out
  }

  count(): number { return this.shapes.length }

  // ───────── private ─────────

  private materialize(r: any): RailwayShape | null {
    const id = r['owl:sameAs']
    const operator = r['odpt:operator']
    const title = r['odpt:railwayTitle']?.ja ?? r['dc:title'] ?? id
    const stationOrder = r['odpt:stationOrder']
    if (!id || !operator || !Array.isArray(stationOrder)) return null

    // [{odpt:station, odpt:index}, ...] を index 順にソート
    const sorted = [...stationOrder].sort((a, b) => (a['odpt:index'] ?? 0) - (b['odpt:index'] ?? 0))
    const coords: [number, number][] = []
    for (const s of sorted) {
      const sid = s['odpt:station']
      if (!sid) continue
      const c = this.stationCoords.get(sid)
      if (!c) continue
      coords.push(c)
    }
    if (coords.length < 2) return null
    return { id, title, operator, coordinates: coords }
  }

  private readCache(): RailwayShapesCache | null {
    try {
      const raw = localStorage.getItem(CACHE_KEY)
      if (!raw) return null
      return JSON.parse(raw) as RailwayShapesCache
    } catch {
      return null
    }
  }

  private writeCache(c: RailwayShapesCache): void {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(c))
    } catch (err) {
      console.warn('[railway-shapes] cache write failed', err)
    }
  }
}
